/**
 * Native refresh-token store (Feb 2026, rev. 2026-02-30).
 *
 * iOS Capacitor Preferences IPC has been observed to stall on cold-start
 * and long-idle resume, holding the auth refresh flow hostage to the
 * platform plugin's wake latency. This module now:
 *
 *   1. Keeps the source of truth in @capacitor/preferences (unchanged).
 *   2. Adds an in-memory mirror that is populated on first read and on
 *      every write. Subsequent reads return from memory and NEVER hit
 *      Capacitor IPC.
 *   3. Provides `primeRefreshTokenCache()` for app-boot pre-warming.
 *   4. Hard-caps every Preferences IPC call with PREFS_IO_TIMEOUT_MS so
 *      a hung plugin can never block the JS event loop. On timeout the
 *      cache stays as last-known-good (or null) and the failure is
 *      surfaced via the returned status enum.
 *
 * Read path latency: <1ms cached, ≤PREFS_IO_TIMEOUT_MS cold. Write path
 * latency: 0ms (cache write) + fire-and-forget Preferences IPC.
 *
 * Behavior contract (unchanged at the consumer):
 *   - Web:    isNative() === false; every function is a no-op.
 *   - Native: getRefreshToken() returns the cached value first.
 *
 * Security note: in-memory cache is local to this app/process; never
 * persisted by us beyond the Preferences write that already exists.
 * Cleared by clearRefreshToken() and (defensively) by logout.
 */

import { Capacitor } from '@capacitor/core';
import {
  recordRefreshTokenWrite as _diagRecordWrite,
  recordRefreshTokenRead as _diagRecordRead,
  recordRefreshTokenClear as _diagRecordClear,
  recordRefreshTokenPresence as _diagRecordPresence,
} from './diagnostics';

const REFRESH_TOKEN_KEY = 'celey_refresh_token';
const PREFS_IO_TIMEOUT_MS = 1500;
// Defense-in-depth: localStorage mirror key for native builds. WKWebView
// localStorage is in-process and never stalls; survives JS context
// teardown provided iOS doesn't purge WebView storage (rare on iOS 17+).
// Read path falls back to this mirror when Preferences IPC fails AND
// the in-memory cache is empty. See refresh-token-persistence-fix-0531.
const LS_MIRROR_KEY = 'celey_refresh_token_ls_mirror';
// Retry profile for Preferences.set. iOS Preferences (NSUserDefaults
// under the hood) is usually <50ms but can stall 1-2s on cold-start.
// 4 attempts at these delays gives ~5s total worst case while still
// completing quickly on a healthy device. Login() awaits this fully.
const WRITE_RETRY_DELAYS_MS = [0, 250, 750, 1500];

// In-memory mirror — primary read path after first hydration.
let _cachedToken = undefined;       // undefined = not yet hydrated, null = known-absent, string = present
let _hydrationPromise = null;       // single-flight cold read

let _preferencesPromise = null;
function loadPreferences() {
  if (!_preferencesPromise) {
    _preferencesPromise = import('@capacitor/preferences').then(m => m.Preferences);
  }
  return _preferencesPromise;
}

export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Race a promise against a timeout. Resolves with the sentinel on timeout. */
const _withTimeout = (promise, ms, timeoutValue) =>
  Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(timeoutValue), ms)),
  ]);

/**
 * Cold-path read: load Preferences module, fetch the value, populate the
 * cache. Bounded by PREFS_IO_TIMEOUT_MS. Single-flight: concurrent callers
 * await the same hydration promise.
 *
 * Returns one of:
 *   - the stored string token
 *   - null (truly absent)
 *   - undefined (timeout / IPC failure — cache stays undefined, caller
 *     decides whether to treat as missing)
 */
async function _coldRead() {
  if (_hydrationPromise) return _hydrationPromise;
  _hydrationPromise = (async () => {
    try {
      const Preferences = await _withTimeout(loadPreferences(), PREFS_IO_TIMEOUT_MS, '__MODULE_TIMEOUT__');
      if (Preferences === '__MODULE_TIMEOUT__') {
        try { _diagRecordPresence({ persistence: 'module-timeout' }); } catch { /* noop */ }
        return undefined;
      }
      const result = await _withTimeout(
        Preferences.get({ key: REFRESH_TOKEN_KEY }),
        PREFS_IO_TIMEOUT_MS,
        '__GET_TIMEOUT__'
      );
      if (result === '__GET_TIMEOUT__') {
        try { _diagRecordPresence({ persistence: 'get-timeout' }); } catch { /* noop */ }
        return undefined;
      }
      const value = result?.value || null;
      _cachedToken = value;
      try {
        _diagRecordPresence({
          cache: typeof _cachedToken === 'string' ? 'true' : (_cachedToken === null ? 'false' : 'not-hydrated'),
          persistence: value ? 'true' : 'false',
        });
      } catch { /* noop */ }
      return value;
    } catch (e) {
      try { _diagRecordPresence({ persistence: 'error' }); } catch { /* noop */ }
      return undefined;
    } finally {
      // Allow a fresh attempt later if this one failed without populating.
      setTimeout(() => { _hydrationPromise = null; }, 0);
    }
  })();
  return _hydrationPromise;
}

/**
 * Public read. Returns the in-memory token if hydrated; otherwise
 * triggers a single cold read. Returns null on any failure — callers
 * MUST treat null as "no token" and proceed to the controlled re-auth
 * path. The hard timeout in _coldRead() guarantees this function
 * completes within ≤PREFS_IO_TIMEOUT_MS in the worst case.
 */
export async function getRefreshToken(source = 'unknown') {
  if (!isNative()) {
    try { _diagRecordRead({ source, result: 'web-noop' }); } catch { /* noop */ }
    return null;
  }
  if (typeof _cachedToken === 'string') {
    try { _diagRecordRead({ source, result: 'cache-hit' }); } catch { /* noop */ }
    return _cachedToken;
  }
  if (_cachedToken === null) {
    // Cache says known-absent, but the localStorage mirror may still hold
    // a recoverable token if a prior Preferences write timed out. Check
    // it before reporting null — this is the recovery path the audit
    // identified as missing.
    const lsMirror = _readLsMirror();
    if (lsMirror) {
      _cachedToken = lsMirror;
      try { _diagRecordRead({ source, result: 'localstorage-recovery' }); } catch { /* noop */ }
      // Best-effort re-persist to Preferences so future cold reads find it.
      _backgroundRepersist(lsMirror);
      return lsMirror;
    }
    try { _diagRecordRead({ source, result: 'null' }); } catch { /* noop */ }
    return null;
  }
  // First call ever — cold read with hard timeout. May return undefined.
  const value = await _coldRead();
  if (value) {
    try { _diagRecordRead({ source, result: 'cold-read-success' }); } catch { /* noop */ }
    return value;
  }
  // Cold read returned null/undefined. Fall back to localStorage mirror
  // before giving up.
  const lsMirror = _readLsMirror();
  if (lsMirror) {
    _cachedToken = lsMirror;
    try { _diagRecordRead({ source, result: 'localstorage-recovery' }); } catch { /* noop */ }
    _backgroundRepersist(lsMirror);
    return lsMirror;
  }
  let result;
  if (value === undefined) result = 'timeout';
  else result = 'missing';
  try { _diagRecordRead({ source, result }); } catch { /* noop */ }
  return null;
}

const _readLsMirror = () => {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(LS_MIRROR_KEY);
    return v || null;
  } catch { return null; }
};

// Fire-and-forget re-persist. Used after a localStorage recovery to
// promote the value back into Preferences so subsequent cold reads
// don't keep falling back to the mirror.
const _backgroundRepersist = (token) => {
  (async () => {
    try {
      const Preferences = await _withTimeout(loadPreferences(), PREFS_IO_TIMEOUT_MS, '__MODULE_TIMEOUT__');
      if (Preferences === '__MODULE_TIMEOUT__') return;
      await _withTimeout(
        Preferences.set({ key: REFRESH_TOKEN_KEY, value: token }),
        PREFS_IO_TIMEOUT_MS,
        '__SET_TIMEOUT__'
      );
    } catch { /* noop */ }
  })();
};

/**
 * Synchronous read of the cached value only. Used by the boot probe and
 * diagnostics — never blocks on Capacitor IPC.
 *   - returns the cached string if hydrated
 *   - returns null if known-absent
 *   - returns the sentinel 'not-hydrated' if hydration hasn't run yet
 */
export function peekRefreshTokenSync() {
  if (typeof _cachedToken === 'string') return _cachedToken;
  if (_cachedToken === null) return null;
  return 'not-hydrated';
}

/**
 * Boot pre-warm. Triggers the cold read so by the time the user's first
 * long-idle resume happens, the cache is populated and the refresh
 * path skips Capacitor IPC entirely. Fire-and-forget — never throws.
 */
export function primeRefreshTokenCache() {
  if (!isNative()) return;
  _coldRead().catch(() => { /* noop */ });
}

/**
 * Write path. Updates the in-memory mirror IMMEDIATELY so subsequent
 * reads return the new value with zero IPC latency. Then fire-and-forget
 * writes to Capacitor Preferences so the value survives a relaunch.
 * The write itself is bounded but failures are logged, not thrown —
 * callers cannot recover from a stuck IPC anyway.
 */
export async function setRefreshToken(token, source = 'unknown') {
  if (!isNative()) return { ok: false, outcome: 'web-noop' };
  if (!token) {
    await clearRefreshToken('set-empty-input');
    return { ok: false, outcome: 'cleared-empty-input' };
  }

  // ① RAM mirror — guaranteed to succeed, even if everything else fails.
  _cachedToken = token;

  // ② localStorage mirror — defense-in-depth. WKWebView in-process write,
  //    no IPC, no risk of stalling. Survives JS context restart unless
  //    iOS explicitly purges WebView storage (extremely rare).
  let lsMirrorOk = false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LS_MIRROR_KEY, token);
      lsMirrorOk = true;
    }
  } catch { /* localStorage may be disabled in some embedded WebViews */ }

  // ③ Preferences write — retry with backoff until verified or attempts
  //    exhausted. Each attempt is bounded by PREFS_IO_TIMEOUT_MS.
  let outcome = 'cache-only-prefs-unknown';
  let errorRef = null;
  let attempts = 0;
  let lastAttemptDelay = 0;

  for (const delay of WRITE_RETRY_DELAYS_MS) {
    attempts += 1;
    lastAttemptDelay = delay;
    if (delay > 0) {
      await new Promise((r) => setTimeout(r, delay));
    }
    try {
      const Preferences = await _withTimeout(loadPreferences(), PREFS_IO_TIMEOUT_MS, '__MODULE_TIMEOUT__');
      if (Preferences === '__MODULE_TIMEOUT__') {
        outcome = 'cache-only-module-timeout';
        continue;
      }
      const settle = await _withTimeout(
        Preferences.set({ key: REFRESH_TOKEN_KEY, value: token }),
        PREFS_IO_TIMEOUT_MS,
        '__SET_TIMEOUT__'
      );
      if (settle === '__SET_TIMEOUT__') {
        outcome = 'cache-only-prefs-timeout';
        continue;
      }
      // Verify by reading back. If Preferences returns the same string
      // we just wrote, persistence is durable.
      const verify = await _withTimeout(
        Preferences.get({ key: REFRESH_TOKEN_KEY }),
        PREFS_IO_TIMEOUT_MS,
        '__VERIFY_TIMEOUT__'
      );
      if (verify === '__VERIFY_TIMEOUT__') {
        outcome = 'cache-only-verify-timeout';
        continue;
      }
      if (verify?.value !== token) {
        outcome = 'verify-mismatch';
        errorRef = `expected=<len ${token.length}> got=<${verify?.value === null ? 'null' : `len ${verify?.value?.length}`}> attempt=${attempts}`;
        continue;
      }
      outcome = 'success';
      errorRef = null;
      break;
    } catch (e) {
      outcome = 'cache-only-prefs-error';
      errorRef = String(e?.message || e);
      // Keep retrying — a transient IPC blip on one attempt may resolve.
    }
  }

  // Annotate the outcome with retry telemetry so the diagnostic panel
  // shows whether retry succeeded after N attempts.
  const finalOutcome = outcome === 'success' && attempts > 1
    ? `success-after-${attempts}-attempts`
    : outcome;

  try {
    _diagRecordWrite({
      source,
      outcome: finalOutcome,
      error: errorRef ? `${errorRef} | lsMirror=${lsMirrorOk ? 'ok' : 'fail'} | lastDelay=${lastAttemptDelay}ms` : `lsMirror=${lsMirrorOk ? 'ok' : 'fail'}`,
    });
    _diagRecordPresence({
      cache: typeof _cachedToken === 'string' ? 'true' : (_cachedToken === null ? 'false' : 'not-hydrated'),
      persistence: outcome === 'success' ? 'true' : (lsMirrorOk ? 'localstorage-only' : outcome),
    });
  } catch { /* noop */ }

  return {
    ok: outcome === 'success',
    outcome: finalOutcome,
    error: errorRef,
    attempts,
    lsMirrorOk,
  };
}

/**
 * Clear both the in-memory mirror and the persisted value. Always sets
 * the cache to null FIRST so even if the IPC clear hangs, subsequent
 * reads return null. Hardened against transient Preferences IPC failures
 * (matches the prior implementation's intent but with hard timeouts).
 */
export async function clearRefreshToken(reason = 'unknown') {
  if (!isNative()) return;
  _cachedToken = null;
  try { _diagRecordClear({ reason }); } catch { /* noop */ }
  try { _diagRecordPresence({ cache: 'false' }); } catch { /* noop */ }
  // Always clear the localStorage mirror — never leave a recoverable
  // token after an explicit logout/clear path.
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem(LS_MIRROR_KEY); } catch { /* noop */ }
  let Preferences;
  try {
    Preferences = await _withTimeout(loadPreferences(), PREFS_IO_TIMEOUT_MS, '__MODULE_TIMEOUT__');
    if (Preferences === '__MODULE_TIMEOUT__') {
      console.warn('[nativeTokenStore] clearRefreshToken: Preferences module load timed out — cache cleared, persist deferred');
      return;
    }
  } catch (e) {
    console.warn('[nativeTokenStore] clearRefreshToken: Preferences unavailable:', e);
    return;
  }
  try {
    const r1 = await _withTimeout(
      Preferences.remove({ key: REFRESH_TOKEN_KEY }),
      PREFS_IO_TIMEOUT_MS,
      '__REMOVE_TIMEOUT__'
    );
    if (r1 !== '__REMOVE_TIMEOUT__') {
      try { _diagRecordPresence({ persistence: 'false' }); } catch { /* noop */ }
      return;
    }
    console.warn('[nativeTokenStore] clearRefreshToken: remove timed out, retrying');
    const r2 = await _withTimeout(
      Preferences.remove({ key: REFRESH_TOKEN_KEY }),
      PREFS_IO_TIMEOUT_MS,
      '__REMOVE_TIMEOUT__'
    );
    if (r2 !== '__REMOVE_TIMEOUT__') {
      try { _diagRecordPresence({ persistence: 'false' }); } catch { /* noop */ }
      return;
    }
    // Tombstone fallback.
    await _withTimeout(
      Preferences.set({ key: REFRESH_TOKEN_KEY, value: '' }),
      PREFS_IO_TIMEOUT_MS,
      '__TOMBSTONE_TIMEOUT__'
    );
    try { _diagRecordPresence({ persistence: 'tombstoned' }); } catch { /* noop */ }
  } catch (e) {
    console.warn('[nativeTokenStore] clearRefreshToken: error path (cache already cleared):', e);
  }
}
